import React, { useState } from "react";
import { Project } from "@/services/interviewService";
import {
  Folder,
  Archive,
  ChevronDown,
  ChevronRight,
  Share2,
  Plus,
} from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useSidebar } from "@/components/ui/sidebar";

interface ProjectTabsProps {
  ownedProjects: Project[];
  sharedProjects: Project[];
  archivedProjects: Project[];
  selectedProject: string;
  onProjectChange: (projectId: string) => void;
  onToggleProjectStatus: (
    projectId: string,
    newStatus?: "active" | "inactive" | "archived"
  ) => void;
  onAddProject: () => void;
}

const ProjectTabs: React.FC<ProjectTabsProps> = ({
  ownedProjects,
  sharedProjects,
  archivedProjects,
  selectedProject,
  onProjectChange,
  onToggleProjectStatus,
  onAddProject,
}) => {
  const [sharedExpanded, setSharedExpanded] = useState(false);
  const [archivedExpanded, setArchivedExpanded] = useState(false);
  const { state } = useSidebar();
  const isExpanded = state === "expanded";

  return (
    <>
      {/* Add Project Button */}
      <div className="px-3 pt-4 pb-2">
        <SidebarMenuButton
          onClick={onAddProject}
          tooltip="Add Project"
          className="w-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 font-normal transition-all"
        >
          <Plus className={`w-4 h-4 ${isExpanded ? "mr-1" : ""}`} />
          {isExpanded && <span>Add Project</span>}
        </SidebarMenuButton>
      </div>

      {/* My Projects Section */}
      <SidebarGroup className="pt-2">
        {isExpanded && <SidebarGroupLabel>My Projects</SidebarGroupLabel>}
        <SidebarGroupContent>
          <SidebarMenu>
            {ownedProjects.length === 0
              ? isExpanded && (
                  <div className="px-3 py-2 text-sm text-muted-foreground italic">
                    No projects owned
                  </div>
                )
              : ownedProjects.map((project) => (
                  <SidebarMenuItem key={project.id}>
                    <SidebarMenuButton
                      isActive={selectedProject === project.id}
                      onClick={() => onProjectChange(project.id)}
                      tooltip={project.name}
                    >
                      <Folder
                        className={
                          selectedProject === project.id
                            ? "text-ivy-emerald dark:text-emerald-400"
                            : "text-ivy-green dark:text-green-400"
                        }
                      />
                      {isExpanded && (
                        <div className="flex justify-between items-center w-full min-w-0">
                          <span className="truncate flex-1 mr-2">
                            {project.name}
                          </span>
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
                              project.status === "active"
                                ? "bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700 border border-green-300 dark:border-green-600"
                                : "bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600"
                            }`}
                          >
                            {project.status === "active"
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </div>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* Shared Projects Section */}
      <SidebarGroup>
        <button
          onClick={() => setSharedExpanded(!sharedExpanded)}
          className="flex items-center w-full px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent rounded-md transition-colors"
        >
          {sharedExpanded ? (
            <ChevronDown className="w-4 h-4 mr-2 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 mr-2 text-muted-foreground" />
          )}
          {isExpanded && (
            <span className="dark:text-gray-300">Shared With Me</span>
          )}
        </button>

        {sharedExpanded && (
          <SidebarGroupContent>
            <SidebarMenu>
              {sharedProjects.length === 0
                ? isExpanded && (
                    <div className="px-3 py-2 text-sm text-muted-foreground italic">
                      No shared projects
                    </div>
                  )
                : sharedProjects.map((project) => (
                    <SidebarMenuItem key={project.id}>
                      <SidebarMenuButton
                        isActive={selectedProject === project.id}
                        onClick={() => onProjectChange(project.id)}
                        tooltip={project.name}
                      >
                        <Share2
                          className={`w-4 h-4 ${
                            selectedProject === project.id
                              ? "text-slate-blue dark:text-blue-400"
                              : "text-slate-blue/60 dark:text-blue-300"
                          }`}
                        />
                        {isExpanded && (
                          <span className="truncate">{project.name}</span>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
            </SidebarMenu>
          </SidebarGroupContent>
        )}
      </SidebarGroup>

      {/* Archived Projects Section */}
      <SidebarGroup>
        <button
          onClick={() => setArchivedExpanded(!archivedExpanded)}
          className="flex items-center w-full px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent rounded-md transition-colors"
        >
          {archivedExpanded ? (
            <ChevronDown className="w-4 h-4 mr-2 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 mr-2 text-muted-foreground" />
          )}
          {isExpanded && (
            <span className="dark:text-gray-300">Archived Projects</span>
          )}
        </button>

        {archivedExpanded && (
          <SidebarGroupContent>
            <SidebarMenu>
              {archivedProjects.length === 0
                ? isExpanded && (
                    <div className="px-3 py-2 text-sm text-muted-foreground italic">
                      No archived projects
                    </div>
                  )
                : archivedProjects.map((project) => (
                    <SidebarMenuItem key={project.id}>
                      <SidebarMenuButton
                        isActive={selectedProject === project.id}
                        onClick={() => onProjectChange(project.id)}
                        tooltip={project.name}
                        className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                      >
                        <Archive className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                        {isExpanded && (
                          <span className="truncate">{project.name}</span>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
            </SidebarMenu>
          </SidebarGroupContent>
        )}
      </SidebarGroup>
    </>
  );
};

export default ProjectTabs;
